import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const IntegratedInitialAssessmentList = ({ records = [], loading, onAdd, onEdit, onDelete }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Search filtering - checks all columns for the search string
    const filteredRecords = records.filter((r) =>
        Object.values(r).some((v) =>
            String(v ?? "")
                .toLowerCase()
                .includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Confirm Delete",
            text: "Do you want to delete this Assessment?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await onDelete(id); // Calling the unwrap dispatch from parent
                Swal.fire("Deleted!", "Assessment removed successfully.", "success");
            } catch (err) {
                Swal.fire("Error!", err || "Failed to delete record.", "error");
            }
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Integrated Initial Assessments", 14, 10);

        const tableColumn = ["#", "Name", "Age", "Gender", "Date"];
        const tableRows = filteredRecords.map((r, i) => [
            i + 1,
            r.name || "N/A",
            r.age || "N/A",
            r.gender || "N/A",
            r.date || "N/A",
        ]);

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("integrated_assessments.pdf");
    };

    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "70px",
        },
        { name: "Name", selector: (r) => r.name, sortable: true },
        { name: "Age", selector: (r) => r.age, sortable: true },
        { name: "Gender", selector: (r) => r.gender, sortable: true },
        { name: "Date", selector: (r) => r.date, sortable: true },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(row.id)}>
                        <i className="ri-edit-line"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
                        <i className="ri-delete-bin-line"></i>
                    </button>
                </div>
            ),
            width: "130px",
        },
    ];

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink data={filteredRecords} filename="integrated_assessments.csv" className="dropdown-item">
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>
                    <input
                        type="text"
                        placeholder="Search..."
                        className="form-control form-control-sm"
                        style={{ width: "200px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-muted">Showing {start}-{end} of {totalRows}</h6>
                <div>
                    <button className="btn btn-sm btn-primary" onClick={onAdd}>
                        <i className="ri-add-line me-1"></i> Add New

                    </button>
                </div>
            </div>

            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    paginationPerPage={rowsPerPage}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    progressPending={loading}
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={<div className="p-4">No records found</div>}
                />
            </div>
        </div>
    );
};

export default IntegratedInitialAssessmentList;